import { XCloseIcon } from "Icons/XClose";

import { useEffect, useState } from "react";

import { css } from "@emotion/react";

import { Badge } from "components/shared/library/Badge";
import { Button } from "components/shared/library/Button";
import { FieldLabel } from "components/shared/library/FieldLabel";
import { Toggle } from "components/shared/library/Toggle";
import { Tooltip } from "components/shared/library/Tooltip";
import { Typography } from "components/shared/library/Typography";

import { useAlert } from "contexts/alertContext";

import {
  isCandidateInterviewPublished,
  isExistingRequest,
  publishCandidateInterview,
} from "apis/talentHub";

import { down } from "utils/mediaQueryStrings";

import { uiGray } from "constants/colors";

const style = {
  wrapper: css({
    display: "flex",
    gap: 8,
  }),
  centerAlign: css({ alignItems: "center" }),
  topAlign: css({
    alignItems: "start",
    [down("mobile")]: { flexDirection: "column" },
  }),
  noMargin: { main: css({ margin: 0 }) },
  tooltip: css({
    display: "flex",
    gap: 4,
    padding: 12,
    alignItems: "start",
  }),
  tooltipContent: css({
    display: "flex",
    flexDirection: "column",
    gap: 4,
  }),
  fullDescription: css({
    display: "flex",
    gap: 4,
    alignItems: "start",
    justifyContent: "space-between",
    width: "100%",
    [down("mobile")]: { flexDirection: "column" },
  }),
  fullDescriptionContent: css({
    display: "flex",
    flexDirection: "column",
    gap: 4,
    maxWidth: 340,
  }),
};

type Props = {
  interviewFlowId: string;
  candidateId: string;
  display?: "short" | "full";
  isPublishedByDefault?: boolean;
  orgName?: string;
};

export const ShareWithRecruitersToggle = ({
  interviewFlowId,
  candidateId,
  display = "short",
  isPublishedByDefault = false,
  orgName,
}: Props) => {
  const [isPublished, setIsPublished] = useState(isPublishedByDefault);
  const [hasAccessRequest, setHasAccessRequest] = useState(false);
  const [isTooltipDismissed, setIsTooltipDismissed] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const { createAlert } = useAlert();

  useEffect(() => {
    const get = async () => {
      const access = await isExistingRequest({ interviewFlowId, candidateId });
      if (access) setHasAccessRequest(access);
      const published = await isCandidateInterviewPublished({
        interviewFlowId,
        candidateId,
      });
      if (published) setIsPublished(published);
      // If it is published by default, but it is not actually published, publish it
      if (isPublishedByDefault && !published) {
        publishCandidateInterview({
          interviewFlowId,
          candidateId,
          isPublished: isPublishedByDefault,
        });
      }

      setIsLoaded(true);
    };
    get();
  }, [interviewFlowId, candidateId, isPublishedByDefault]);

  // we don't return anything until isPublished is loaded
  if (!isLoaded) return <></>;

  return (
    <div
      css={[
        style.wrapper,
        display == "short" ? style.centerAlign : style.topAlign,
      ]}
    >
      {display == "short" && (
        <FieldLabel
          label={"Share with recruiters"}
          customCss={style.noMargin}
        />
      )}
      <Tooltip
        content={
          <div css={style.tooltip}>
            <div css={style.tooltipContent}>
              <Typography weight="medium" size="XS">
                Grant access to recruiters on Ribbon
              </Typography>
              <Typography color={uiGray[60]} size="XS">
                Someone requested access to your interview. By allowing access,
                you are sharing this interview with all recruiters.
              </Typography>
            </div>
            <Button
              size="XXS"
              IconLeft={XCloseIcon}
              color="gray"
              onClick={() => setIsTooltipDismissed(true)}
              ariaLabel="Close tooltip"
            />
          </div>
        }
        showTooltip={
          hasAccessRequest && !isPublished && !isTooltipDismissed
            ? "always"
            : "never"
        }
        position={"bottom"}
      >
        <Toggle
          label={"Share with recruiters"}
          checked={isPublished}
          onChange={(val) => {
            setIsPublished(val);
            publishCandidateInterview({
              interviewFlowId,
              candidateId,
              isPublished: !!val,
            });
            createAlert("success", "Preferences were successfully updated");
          }}
          displayLabel={false}
          tooltipPosition="bottom"
          tooltipText="Grant access to recruiters on Ribbon"
        ></Toggle>
      </Tooltip>
      {display == "full" && (
        <div css={style.fullDescription}>
          <div css={style.fullDescriptionContent}>
            <Typography weight="medium">
              Share this interview with Ribbon&apos;s recruiter network
            </Typography>
            {orgName && (
              <Typography size="XS" color={uiGray[70]}>
                {`This interview is exclusively available to ${orgName} for the
            first 14 days. After that, it will be shared with other recruiters
            on Ribbon.`}
              </Typography>
            )}
          </div>
          {isPublished && <Badge label={"Enabled"} color="green" />}
          {!isPublished && <Badge label={"Disabled"} color="grey" />}
        </div>
      )}
    </div>
  );
};
